import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Calculator,
  Users,
  BarChart3,
  FileText,
  Shield,
  Settings,
  TrendingUp,
  Wallet,
  BookOpen,
  Calendar,
  Clock,
  Award,
  ClipboardList,
  PieChart,
  Brain,
  UserCheck,
  Landmark,
  Receipt,
  Scale,
  Zap,
  BadgeCheck,
  Briefcase,
  GraduationCap,
  CalendarDays,
  PenTool,
  Leaf,
  MessageSquare,
  Star,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import api from '../lib/api';

/* ------------------------------------------------------------------ */
/*  Reveal animation hook                                              */
/* ------------------------------------------------------------------ */
const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
};

/* ------------------------------------------------------------------ */
/*  Constants: gradients per app                                       */
/* ------------------------------------------------------------------ */
const APP_GRADIENTS = {
  'nexus-compta': 'from-[#2563eb] to-[#1e3a8a]',
  'nexus-sirh': 'from-[#10b981] to-[#065f46]',
};

const APP_GRADIENTS_LIGHT = {
  'nexus-compta': 'from-blue-50 to-blue-100',
  'nexus-sirh': 'from-emerald-50 to-emerald-100',
};

const APP_ACCENT = {
  'nexus-compta': {
    border: 'border-blue-500',
    bg: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    text: 'text-blue-600',
    ring: 'ring-blue-500',
    hover: 'hover:bg-blue-700',
    btnBg: 'bg-blue-600 hover:bg-blue-700',
    badgeBg: 'bg-blue-100 text-blue-700',
  },
  'nexus-sirh': {
    border: 'border-emerald-500',
    bg: 'bg-emerald-500',
    bgLight: 'bg-emerald-50',
    text: 'text-emerald-600',
    ring: 'ring-emerald-500',
    hover: 'hover:bg-emerald-700',
    btnBg: 'bg-emerald-600 hover:bg-emerald-700',
    badgeBg: 'bg-emerald-100 text-emerald-700',
  },
};

/* ------------------------------------------------------------------ */
/*  Feature module → icon mapping                                      */
/* ------------------------------------------------------------------ */
const MODULE_ICONS = {
  dashboard: BarChart3,
  suppliers: Users,
  fournisseurs: Users,
  invoices: FileText,
  factures: FileText,
  clients: Briefcase,
  comptabilite: BookOpen,
  reports: PieChart,
  validation: BadgeCheck,
  analytical: TrendingUp,
  budgets: Wallet,
  tresorerie: Landmark,
  admin: Settings,
  conformite: Shield,
  einvoicing: Receipt,
  employees: UserCheck,
  conges: Calendar,
  absences: Calendar,
  habilitations: Award,
  documents: ClipboardList,
  payroll: Calculator,
  formations: GraduationCap,
  planning: Clock,
  expenses: Receipt,
  entretiens: MessageSquare,
  reporting: PieChart,
  emargement: PenTool,
  rse: Leaf,
  calendar: CalendarDays,
  ai_chat: Brain,
};

const MODULE_DESCRIPTIONS = {
  dashboard: 'Tableau de bord avec KPIs en temps reel',
  suppliers: 'Gestion complete des fournisseurs',
  fournisseurs: 'Gestion complete des fournisseurs',
  invoices: 'Facturation et suivi des paiements',
  factures: 'Facturation et suivi des paiements',
  clients: 'Gestion de la base clients',
  comptabilite: 'Plan comptable, ecritures, grand livre',
  reports: 'Rapports et exports personnalisables',
  validation: 'Workflows de validation multi-niveaux',
  analytical: 'Comptabilite analytique par centres de couts',
  budgets: 'Suivi budgetaire en temps reel',
  tresorerie: 'Tresorerie et previsions de cash-flow',
  admin: 'Administration et gestion des droits',
  conformite: 'Conformite fiscale et reglementaire',
  einvoicing: 'E-invoicing reforme 2026',
  employees: 'Fiches employes completes',
  conges: 'Gestion des conges et absences',
  absences: 'Gestion des conges et absences',
  habilitations: 'Suivi des habilitations et certifications',
  documents: 'Generation de documents RH',
  payroll: 'Preparation de la paie',
  formations: 'Gestion des formations',
  planning: 'Planning et gestion des temps',
  expenses: 'Notes de frais',
  entretiens: 'Entretiens annuels',
  reporting: 'Reporting RH avance',
  emargement: 'Emargement et signatures',
  rse: 'RSE et indicateurs sociaux',
  calendar: 'Calendrier des absences',
  ai_chat: 'Assistant IA integre',
};

/* ------------------------------------------------------------------ */
/*  FAQ data per app                                                   */
/* ------------------------------------------------------------------ */
const FAQ_DATA = {
  'nexus-compta': [
    {
      q: "Qu'est-ce qui est inclus dans la periode d'essai ?",
      a: "La periode d'essai de 7 jours vous donne acces a l'ensemble des fonctionnalites de Nexus Compta, sans limitation. Vous pouvez tester toutes les fonctions de facturation, comptabilite, tresorerie et reporting sans avoir a fournir de carte bancaire.",
    },
    {
      q: 'Puis-je importer mes donnees existantes ?',
      a: "Oui, Nexus Compta prend en charge l'import de fichiers FEC (Fichier des Ecritures Comptables), CSV, et d'autres formats standards. Notre assistant d'import vous guide pas a pas pour migrer vos donnees en toute securite.",
    },
    {
      q: 'La facturation electronique 2026 est-elle incluse ?',
      a: "Oui, la conformite a la reforme de la facturation electronique (septembre 2026) est incluse dans les forfaits Pro et Enterprise. Vous pourrez emettre et recevoir des factures au format Factur-X via les plateformes de dematerialisation partenaires (PDP).",
    },
    {
      q: "Combien d'utilisateurs peuvent utiliser la plateforme ?",
      a: "Le nombre d'utilisateurs depend de votre forfait. Le forfait Starter permet jusqu'a 3 utilisateurs, le forfait Pro jusqu'a 10, et le forfait Enterprise offre un nombre illimite d'utilisateurs. Vous pouvez changer de forfait a tout moment.",
    },
    {
      q: 'Comment fonctionne le support ?',
      a: "Notre equipe de support francaise est disponible par email et par chat integre a l'application. Nous garantissons un temps de reponse inferieur a 24h. Les forfaits Enterprise beneficient d'un support prioritaire avec un interlocuteur dedie.",
    },
  ],
  'nexus-sirh': [
    {
      q: "Qu'est-ce qui est inclus dans la periode d'essai ?",
      a: "La periode d'essai de 7 jours vous donne acces a l'ensemble des fonctionnalites de Nexus SIRH, sans limitation. Vous pouvez tester la gestion des employes, conges, habilitations et documents sans fournir de carte bancaire.",
    },
    {
      q: 'Puis-je gerer la paie avec Nexus SIRH ?',
      a: "Oui, Nexus SIRH inclut un module de preparation de la paie qui s'integre avec les principaux prestataires de paie (Sage, Cegid, ADP...). Vous preparez les elements variables et exportez les donnees vers votre solution de paie.",
    },
    {
      q: 'Les bulletins de paie sont-ils generes automatiquement ?',
      a: "Nexus SIRH genere automatiquement les documents RH au format PDF : attestations, contrats, avenants et certificats. Pour les bulletins de paie, nous nous integrons a votre prestataire de paie qui gere la generation des bulletins conformes.",
    },
    {
      q: 'Comment gerer les habilitations ?',
      a: "Le module Habilitations vous permet de suivre toutes les certifications et habilitations de vos employes. Vous recevez des alertes automatiques avant expiration, pouvez telecharger les justificatifs et generer des rapports de conformite.",
    },
    {
      q: "Le portail employe est-il inclus ?",
      a: "Oui, le portail employe est inclus dans les forfaits Pro et Enterprise. Chaque employe dispose d'un acces personnel pour consulter ses conges, soumettre ses demandes, telecharger ses documents et mettre a jour ses informations personnelles.",
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

/** Skeleton loader while fetching */
const SkeletonLoader = () => (
  <div className="min-h-screen bg-gray-50 animate-pulse">
    {/* Hero skeleton */}
    <div className="h-[480px] bg-gray-200" />
    {/* Features skeleton */}
    <div className="max-w-7xl mx-auto px-4 py-20">
      <div className="h-8 bg-gray-200 rounded w-80 mx-auto mb-4" />
      <div className="h-4 bg-gray-200 rounded w-60 mx-auto mb-12" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-40 bg-gray-200 rounded-xl" />
        ))}
      </div>
    </div>
    {/* Pricing skeleton */}
    <div className="max-w-7xl mx-auto px-4 py-20">
      <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-12" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-96 bg-gray-200 rounded-xl" />
        ))}
      </div>
    </div>
  </div>
);

/** Error state */
const ErrorState = ({ message, onRetry, t }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center max-w-md px-4">
      <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
        <span className="text-3xl">!</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">Application introuvable</h1>
      <p className="text-gray-600 mb-8">
        {message || "L'application demandee n'existe pas ou n'est plus disponible."}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
          >
            {t('common.retry')}
          </button>
        )}
        <Link
          to="/"
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          Retour a l'accueil
        </Link>
      </div>
    </div>
  </div>
);

/** FAQ accordion item */
const FaqItem = ({ question, answer, accent }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden transition-all">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 pr-4">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-5 text-gray-600 leading-relaxed">{answer}</div>
      </div>
    </div>
  );
};

/** Feature card */
const FeatureCard = ({ moduleName, accent, index }) => {
  const [ref, visible] = useReveal(0.1);
  const Icon = MODULE_ICONS[moduleName] || Sparkles;
  const description = MODULE_DESCRIPTIONS[moduleName] || moduleName;

  return (
    <div
      ref={ref}
      className={`bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      <div
        className={`w-12 h-12 rounded-xl ${accent.bgLight} flex items-center justify-center mb-4`}
      >
        <Icon className={`w-6 h-6 ${accent.text}`} />
      </div>
      <h3 className="font-semibold text-gray-900 mb-2 capitalize">
        {moduleName.replace(/_/g, ' ')}
      </h3>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

/** Pricing card */
const PricingCard = ({ plan, isAnnual, appCode, accent, isPopular }) => {
  const monthlyPrice = isAnnual
    ? Math.round(plan.price_annual_ht / 12)
    : plan.price_monthly_ht;
  const annualTotal = plan.price_annual_ht;
  const monthlyCost = plan.price_monthly_ht;
  const savings =
    isAnnual && monthlyCost > 0
      ? Math.round(((monthlyCost * 12 - annualTotal) / (monthlyCost * 12)) * 100)
      : 0;

  return (
    <div
      className={`relative bg-white rounded-2xl border-2 p-8 flex flex-col transition-all duration-300 ${
        isPopular
          ? `${accent.border} shadow-xl scale-105 z-10`
          : 'border-gray-200 shadow-sm hover:shadow-lg'
      }`}
    >
      {isPopular && (
        <div
          className={`absolute -top-4 left-1/2 -translate-x-1/2 ${accent.bg} text-white text-sm font-semibold px-4 py-1 rounded-full`}
        >
          Populaire
        </div>
      )}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
        {plan.max_users && (
          <p className="text-sm text-gray-500">
            Jusqu'a {plan.max_users} utilisateurs
          </p>
        )}
      </div>
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-extrabold text-gray-900">{monthlyPrice}</span>
          <span className="text-lg text-gray-500">&euro; HT/mois</span>
        </div>
        {isAnnual && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-500">soit {annualTotal}&euro;/an</span>
            {savings > 0 && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${accent.badgeBg}`}>
                -{savings}% d'economie
              </span>
            )}
          </div>
        )}
      </div>
      <ul className="space-y-3 mb-8 flex-1">
        {(plan.modules || []).map((mod) => (
          <li key={mod} className="flex items-start gap-3">
            <Check className={`w-5 h-5 ${accent.text} flex-shrink-0 mt-0.5`} />
            <span className="text-sm text-gray-700 capitalize">{mod.replace(/_/g, ' ')}</span>
          </li>
        ))}
      </ul>
      <Link
        to={`/register?app=${appCode}&plan=${plan.code}`}
        className={`block text-center py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
          isPopular
            ? `${accent.btnBg} text-white shadow-lg hover:shadow-xl`
            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
        }`}
      >
        Choisir ce forfait
      </Link>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
export default function AppDetail() {
  const { appCode } = useParams();
  const { t } = useTranslation();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAnnual, setIsAnnual] = useState(true);

  // Section refs for reveal
  const [featuresRef, featuresVisible] = useReveal(0.05);
  const [pricingRef, pricingVisible] = useReveal(0.05);
  const [faqRef, faqVisible] = useReveal(0.05);
  const [ctaRef, ctaVisible] = useReveal(0.1);

  const fetchApp = () => {
    setLoading(true);
    setError(null);
    api
      .get('/api/apps/' + appCode)
      .then((res) => {
        setApp(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.status === 404 ? '404' : 'error');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchApp();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appCode]);

  if (loading) return <SkeletonLoader />;
  if (error) return <ErrorState message={error === '404' ? null : t('common.error')} onRetry={fetchApp} t={t} />;
  if (!app) return <ErrorState t={t} />;

  const gradient = APP_GRADIENTS[app.code] || APP_GRADIENTS['nexus-compta'];
  const gradientLight = APP_GRADIENTS_LIGHT[app.code] || APP_GRADIENTS_LIGHT['nexus-compta'];
  const accent = APP_ACCENT[app.code] || APP_ACCENT['nexus-compta'];

  // Get features from the largest package (last one = enterprise)
  const largestPackage =
    app.packages && app.packages.length > 0
      ? app.packages[app.packages.length - 1]
      : null;
  const allModules = largestPackage?.modules || [];

  // FAQ for current app
  const faqItems = FAQ_DATA[app.code] || FAQ_DATA['nexus-compta'] || [];

  // Other app for cross-linking
  const otherApp =
    app.code === 'nexus-compta'
      ? { code: 'nexus-sirh', name: 'Nexus SIRH' }
      : { code: 'nexus-compta', name: 'Nexus Compta' };

  // App icon component
  const AppIcon =
    app.icon === 'calculator' ? Calculator : app.icon === 'users' ? Users : Calculator;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ============================================================ */}
      {/*  Back navigation                                             */}
      {/* ============================================================ */}
      <div className={`bg-gradient-to-r ${gradient}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux applications
          </Link>
        </div>

        {/* ============================================================ */}
        {/*  Section 1: Hero                                             */}
        {/* ============================================================ */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          {/* App icon */}
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-3xl mb-8 shadow-2xl ring-1 ring-white/30">
            <AppIcon className="w-12 h-12 text-white" />
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-6 tracking-tight">
            {app.name}
          </h1>

          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
            {app.description}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={`/register?app=${app.code}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-bold rounded-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200"
            >
              Commencer l'essai gratuit
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            {t('common.free_trial')}
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/*  Section 2: Features detaillees                               */}
      {/* ============================================================ */}
      <section
        ref={featuresRef}
        className={`py-20 md:py-28 transition-all duration-700 ${
          featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Decouvrez l'ensemble des fonctionnalites incluses dans {app.name}.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allModules.map((mod, i) => (
              <FeatureCard key={mod} moduleName={mod} accent={accent} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  Section 3: Plans et tarifs                                   */}
      {/* ============================================================ */}
      <section
        ref={pricingRef}
        className={`py-20 md:py-28 bg-gradient-to-b ${gradientLight} transition-all duration-700 ${
          pricingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Choisissez votre forfait
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Tous les forfaits incluent 7 jours d'essai gratuit, sans engagement.
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center bg-white rounded-xl p-1.5 shadow-sm border border-gray-200">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  !isAnnual
                    ? `${accent.btnBg} text-white shadow-md`
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  isAnnual
                    ? `${accent.btnBg} text-white shadow-md`
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annuel
              </button>
            </div>
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 ${
              app.packages?.length >= 3 ? 'lg:grid-cols-3' : ''
            } ${
              app.packages?.length >= 4 ? 'xl:grid-cols-4' : ''
            } gap-8 items-start`}
          >
            {(app.packages || []).map((plan) => (
              <PricingCard
                key={plan.code}
                plan={plan}
                isAnnual={isAnnual}
                appCode={app.code}
                accent={accent}
                isPopular={plan.code === 'pro'}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  Section 4: FAQ                                               */}
      {/* ============================================================ */}
      <section
        ref={faqRef}
        className={`py-20 md:py-28 transition-all duration-700 ${
          faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Questions frequentes
            </h2>
            <p className="text-lg text-gray-600">
              Trouvez rapidement les reponses a vos questions.
            </p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} accent={accent} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  Section 5: CTA Final                                         */}
      {/* ============================================================ */}
      <section
        ref={ctaRef}
        className={`transition-all duration-700 ${
          ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className={`bg-gradient-to-r ${gradient}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6">
              Pret a essayer {app.name} ?
            </h2>
            <p className="text-lg text-white/80 mb-10 max-w-xl mx-auto">
              Lancez votre essai gratuit de 7 jours et decouvrez comment {app.name} peut
              simplifier votre quotidien.
            </p>
            <Link
              to={`/register?app=${app.code}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-bold rounded-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200"
            >
              Commencer l'essai gratuit
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  Section 6: Footer with cross-links                           */}
      {/* ============================================================ */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors font-medium"
              >
                Accueil
              </Link>
              <span className="text-gray-300">|</span>
              <Link
                to={`/apps/${otherApp.code}`}
                className={`text-sm ${accent.text} hover:underline font-medium`}
              >
                Decouvrir {otherApp.name}
              </Link>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <Link to="/cgu" className="hover:text-gray-600 transition-colors">
                CGU
              </Link>
              <Link to="/confidentialite" className="hover:text-gray-600 transition-colors">
                Confidentialite
              </Link>
              <Link to="/mentions-legales" className="hover:text-gray-600 transition-colors">
                Mentions legales
              </Link>
            </div>
            <p className="text-sm text-gray-400">
              {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
